import React, { forwardRef } from 'react';
import { InputField } from './components/InputField';
import { InputLabel } from './components/InputLabel';
import { InputIcon } from './components/InputIcon';
import { ErrorMessage } from './components/ErrorMessage';
import { InputWrapper } from './components/InputWrapper';
import { useInputAccessibility } from '../../hooks/useInputAccessibility';
import './../../scss/ui-input.scss';

export interface UIInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  errorMessage?: string;
  error?: boolean;
  icon?: React.ReactNode;
  /** Optional decorative icon rendered inside the field's leading edge. */
  leadingIcon?: React.ReactNode;
  /** Marks the field as successfully filled — adds `ui-input--valid` for theming. */
  valid?: boolean;
  iconPosition?: 'left' | 'right';
  onIconClick?: () => void;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'outlined' | 'filled';
  containerClassName?: string;
}

export const UIInput = forwardRef<HTMLInputElement, UIInputProps>(
  ({
    label,
    errorMessage,
    error,
    icon,
    leadingIcon,
    valid,
    iconPosition = 'right',
    onIconClick,
    size = 'md',
    variant = 'default',
    containerClassName,
    required,
    disabled,
    className,
    id,
    ...inputProps
  }, ref) => {
    const { inputId, errorId, accessibilityProps } = useInputAccessibility({
      id,
      label,
      errorMessage: error ? errorMessage : undefined,
      required,
      disabled
    });

    const containerClasses = [
      'ui-input',
      valid && !error && 'ui-input--valid',
      leadingIcon && 'ui-input--with-leading-icon',
      icon && 'ui-input--with-trailing-icon',
      containerClassName
    ].filter(Boolean).join(' ');

    const fieldClasses = [
      className,
      leadingIcon && 'ui-input__field--with-leading-icon'
    ].filter(Boolean).join(' ') || undefined;

    return (
      <div className={containerClasses}>
        {label && (
          <InputLabel htmlFor={inputId} required={required}>
            {label}
          </InputLabel>
        )}
        
        <InputWrapper 
          size={size} 
          variant={variant} 
          hasError={error} 
          disabled={disabled}
        >
          {leadingIcon && (
            <InputIcon icon={leadingIcon} position="left" />
          )}

          <InputField
            ref={ref}
            hasError={error}
            hasIcon={!!icon}
            disabled={disabled}
            className={fieldClasses}
            {...accessibilityProps}
            {...inputProps}
          />

          {icon && (
            <InputIcon 
              icon={icon} 
              position={iconPosition} 
              onClick={onIconClick} 
            />
          )}
        </InputWrapper>

        <ErrorMessage 
          message={errorMessage} 
          show={error}
          id={errorId}
        />
      </div>
    );
  }
);

UIInput.displayName = 'UIInput';

export { InputField } from './components/InputField';
export { InputLabel } from './components/InputLabel';
export { InputIcon } from './components/InputIcon';
export { ErrorMessage } from './components/ErrorMessage';
export { InputWrapper } from './components/InputWrapper';
export { useInputAccessibility } from '../../hooks/useInputAccessibility';

export default UIInput;
